import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { BonusStrategy, BonusType, BonusConfig, BonusResult, AmountConstraints } from './interfaces/bonus.interface';
import { BienvenidaBonusStrategy } from './strategies/bienvenida-bonus.strategy';
import { EscalonadoBonusStrategy } from './strategies/escalonado-bonus.strategy';
import { PorcentualBonusStrategy } from './strategies/porcentual-bonus.strategy';
import { CdtRegaloBonusStrategy } from './strategies/cdt-regalo-bonus.strategy';
import { RuletteBonusStrategy } from './strategies/rulette-bonus.strategy';
import { YoMeLlamoCdtBonusStrategy } from './strategies/yo-me-llamo-cdt-bonus.strategy';
import { DiasAzulesBonusStrategy } from './strategies/dias-azules-bonus.strategy';
import { Iaf2026BonusStrategy } from './strategies/iaf-2026-bonus.strategy';
import { Moneycon2026BonusStrategy } from './strategies/moneycon-2026-bonus.strategy';
import { BlueDays } from '@utils/blue-days';
import { IndiceArrepentimientoFinanciero } from '@utils/indece-arrepentimiento-financiero';
import { CookieUtil } from '@utils/cookies';
import { LocalStorageUtil } from '@utils/localstorage';
import { RouletteService } from '../roulette/roulette.service';
import { WheelOption } from '@interface/ruleta.interface';
@Injectable({
  providedIn: 'root',
})
export class BonusService {
  private strategies = new Map<BonusType, BonusStrategy>();
  private currentBonus$ = new BehaviorSubject<BonusResult | null>(null);
  private activeBonusConfig$ = new BehaviorSubject<BonusConfig | null>(null);
  private activeBonusConfig: BonusConfig | null = null;

  // Variable global para controlar el comportamiento de términos inválidos
  private globalBlockInvalidTerms: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private cookies: CookieUtil,
    private localStorage: LocalStorageUtil,
    private rouletteService: RouletteService,
    private blueDays: BlueDays,
    private iafService: IndiceArrepentimientoFinanciero,
  ) {
    this.initializeStrategies();
    this.initializeFromRoute();
  }

  /**
   * Inicializa automáticamente el servicio leyendo los query parameters
   */
  private initializeFromRoute(): void {
    this.route.queryParams.subscribe((params: any) => {
      this.initializeFromQueryParams(params);
    });
  }

  private initializeStrategies(): void {
    this.strategies.set(BonusType.BIENVENIDA, new BienvenidaBonusStrategy());
    this.strategies.set(BonusType.ESCALONADO, new EscalonadoBonusStrategy());
    this.strategies.set(BonusType.PORCENTUAL, new PorcentualBonusStrategy());
    this.strategies.set(BonusType.CDT_REGALO, new CdtRegaloBonusStrategy());
    this.strategies.set(BonusType.RULETA, new RuletteBonusStrategy());
    this.strategies.set(BonusType.YO_ME_LLAMO_CDT, new YoMeLlamoCdtBonusStrategy());
    this.strategies.set(BonusType.DIAS_AZULES, new DiasAzulesBonusStrategy(this.blueDays));
    this.strategies.set(BonusType.IAF_2026, new Iaf2026BonusStrategy(this.iafService));
    this.strategies.set(BonusType.MONEYCON_2026, new Moneycon2026BonusStrategy());
  }

  /**
   * Establece la configuración del bono activo desde query params
   */
  setBonusConfig(config: BonusConfig | null): void {
    this.activeBonusConfig$.next(config);

    if (!config) {
      this.currentBonus$.next(null);
    }
  }

  /**
   * Obtiene la configuración del bono activo
   */
  getActiveBonusConfig(): Observable<BonusConfig | null> {
    return this.activeBonusConfig$.asObservable();
  }

  /**
   * Evalúa el bono basado en el monto ingresado
   */
  evaluateBonus(config: BonusConfig): Observable<BonusResult> {
    const strategy = this.strategies.get(config.type);

    if (!strategy) {
      const nullResult: BonusResult = {
        isValid: false,
        bonusAmount: 0,
        message: '',
        confettiType: '',
      };
      this.currentBonus$.next(nullResult);
      return of(nullResult);
    }

    // Preparar extraParams según el tipo de bono
    const extraParams: any = {
      valorCdtRegalo: config.valorCdtRegalo,
    };

    // Para bonos de ruleta, agregar el premio ganado
    if (config.type === BonusType.RULETA && config.roulettePrize) {
      extraParams.roulettePrize = config.roulettePrize;
    }

    const termDays = config.minTermDays || 360;
    const result = strategy.calculateBonus(config.amount, termDays, extraParams);

    this.currentBonus$.next(result);
    return of(result);
  }

  /**
   * Obtiene el resultado del bono actual
   */
  getCurrentBonus(): Observable<BonusResult | null> {
    return this.currentBonus$.asObservable();
  }

  /**
   * Obtiene el valor actual del bono de forma síncrona
   * @returns BonusResult actual o null
   */
  getCurrentBonusValue(): BonusResult | null {
    return this.currentBonus$.value;
  }

  /**
   * Obtiene el valor actual de la configuración de bono de forma síncrona
   * @returns BonusConfig actual o null
   */
  getActiveBonusConfigValue(): BonusConfig | null {
    return this.activeBonusConfig$.value;
  }

  /**
   * Activa una estrategia de bono personalizada desde componentes
   * @param strategyType Nombre de la estrategia a activar
   * @param amount Monto inicial para evaluar (opcional, se lee desde cookies si no se proporciona)
   */
  activateCustomStrategy(strategyType: string, amount?: number, termDays?: number): void {
    let bonusType: BonusType | null = null;

    switch (strategyType) {
      case 'YoMeLlamoCDT':
        bonusType = BonusType.YO_ME_LLAMO_CDT;
        break;
      case 'DiasAzules':
        bonusType = BonusType.DIAS_AZULES;
        break;
      case 'IAF2026':
        bonusType = BonusType.IAF_2026;
        break;
      case 'Moneycon2026':
        bonusType = BonusType.MONEYCON_2026;
        break;
      default:
        console.warn(`Estrategia personalizada no reconocida: ${strategyType}`);
        return;
    }

    if (bonusType) {
      const finalAmount = amount !== undefined ? amount : this.getAmountFromCookie();

      const customConfig: BonusConfig = {
        type: bonusType,
        amount: finalAmount,
        termDays: termDays,
      };

      this.activeBonusConfig = customConfig;
      this.activeBonusConfig$.next(customConfig);
      this.evaluateBonusForAmount(finalAmount, termDays);

      // Guardar configuración en localStorage para recuperación posterior
      this.storeBonusConfig(customConfig);

      // Configurar modo advertencia para términos
      this.setBlockInvalidTermsMode(false);
    }
  }

  /**
   * Detecta automáticamente el tipo de bono desde query parameters
   * y configura el servicio automáticamente (método interno)
   * Prioriza localStorage para mantener contexto específico de campañas
   */
  private initializeFromQueryParams(queryParams: any): void {
    const bonusFromQuery = this.detectBonusFromParams(queryParams);
    const storedBonusConfig = this.getStoredBonusConfig();

    // Determinar configuración final usando lógica híbrida
    const finalBonusConfig = this.resolveHybridBonusConfig(bonusFromQuery, storedBonusConfig, queryParams);

    if (finalBonusConfig) {
      this.activeBonusConfig = finalBonusConfig;
      this.activeBonusConfig$.next(finalBonusConfig);

      // Guardar configuración final en localStorage para recuperación posterior
      this.storeBonusConfig(finalBonusConfig);

      // Para CDT Regalo, evaluar automáticamente con el valor configurado
      if (finalBonusConfig.type === BonusType.CDT_REGALO && finalBonusConfig.valorCdtRegalo) {
        this.evaluateBonusForAmount(finalBonusConfig.valorCdtRegalo);
      } else {
        // Para otros tipos, evaluar con el amount inicial
        this.evaluateBonusForAmount(finalBonusConfig.amount);
      }
    } else {
      // Si no hay query params de bono, limpiar configuración
      this.clearBonus();
    }
  }

  /**
   * Obtiene el amount desde la cookie
   */
  private getAmountFromCookie(): number {
    const amountFromCookie = this.cookies.get('amount');
    if (amountFromCookie) {
      const amount = Number(amountFromCookie);
      return isNaN(amount) ? 0 : amount; // Valor por defecto si no es un número válido
    }
    return 0; // Valor por defecto si no existe la cookie
  }

  /**
   * Método simplificado para evaluar bonos
   * Si no se proporciona amount, lo lee automáticamente desde la cookie
   */
  evaluateBonusForAmount(amount?: number, termDays?: number): void {
    if (!this.activeBonusConfig) {
      // Si no hay configuración activa, limpiar bonus
      this.currentBonus$.next(null);
      return;
    }

    // Si no se proporciona amount, obtenerlo desde la cookie
    const finalAmount = amount !== undefined ? amount : this.getAmountFromCookie();

    // Actualizar el monto en la configuración
    const updatedConfig: BonusConfig = {
      ...this.activeBonusConfig,
      amount: finalAmount,
      termDays: termDays !== undefined ? termDays : this.activeBonusConfig.termDays,
    };

    const strategy = this.strategies.get(updatedConfig.type);

    if (!strategy) {
      this.currentBonus$.next(null);
      return;
    }

    // Preparar extraParams según el tipo de bono
    const extraParams: any = {
      valorCdtRegalo: updatedConfig.valorCdtRegalo,
    };

    // Para bonos de ruleta, agregar el premio ganado
    if (updatedConfig.type === BonusType.RULETA && updatedConfig.roulettePrize) {
      extraParams.roulettePrize = updatedConfig.roulettePrize;
    }

    // Use termDays from config, fallback to minTermDays or 360
    const days = updatedConfig.termDays !== undefined ? updatedConfig.termDays : (updatedConfig.minTermDays !== undefined ? updatedConfig.minTermDays : 360);
    const result = strategy.calculateBonus(updatedConfig.amount, days, extraParams);

    // Enriquecer el resultado con información adicional para la UI
    const enrichedResult: BonusResult = {
      ...result,
      // Para bonos de ruleta, preservar el bonusTag específico de la estrategia
      // Para otros tipos de bono, usar el tag genérico
      bonusTag:
        updatedConfig.type === BonusType.RULETA && result.bonusTag
          ? result.bonusTag
          : this.getBonusTag(updatedConfig.type),
      displayAmount: this.formatAmount(result.bonusAmount),
      blockInvalidTerms: this.globalBlockInvalidTerms, // Incluir configuración de bloqueo
    };

    this.currentBonus$.next(enrichedResult);
  }

  /**
   * Verifica si hay un bono activo
   */
  hasActiveBonus(): boolean {
    return this.activeBonusConfig !== null;
  }

  /**
   * Obtiene las restricciones de amount para el bono activo
   */
  getActiveAmountConstraints(): AmountConstraints | null {
    if (!this.activeBonusConfig) {
      return null;
    }

    const strategy = this.strategies.get(this.activeBonusConfig.type);
    if (!strategy) {
      return null;
    }

    // Preparar extraParams según el tipo de bono
    const extraParams: any = {
      valorCdtRegalo: this.activeBonusConfig.valorCdtRegalo,
    };

    // Para bonos de ruleta, agregar el premio ganado
    if (this.activeBonusConfig.type === BonusType.RULETA && this.activeBonusConfig.roulettePrize) {
      extraParams.roulettePrize = this.activeBonusConfig.roulettePrize;
    }

    return strategy.getAmountConstraints(extraParams);
  }

  /**
   * Limpia toda la configuración de bonos
   * @param clearStored Si es true, también elimina la configuración almacenada para reactivación
   */
  clearBonus(clearStored: boolean = false): void {
    this.activeBonusConfig = null;
    this.activeBonusConfig$.next(null);
    this.currentBonus$.next(null);

    if (clearStored) {
      this.clearStoredBonusConfig();
    }
  }

  /**
   * Detecta automáticamente el tipo de bono desde los query parameters
   */
  private detectBonusFromParams(params: any): BonusConfig | null {
    const query_bono = params.bono;

    if (query_bono == 'bono_de_bienvenida') {
      return this.createBonusConfig(BonusType.BIENVENIDA, params);
    } else if (query_bono == 'bono_escalonado') {
      return this.createBonusConfig(BonusType.ESCALONADO, params);
    } else if (query_bono == 'bono_porcentual') {
      return this.createBonusConfig(BonusType.PORCENTUAL, params);
    } else if (query_bono == 'bono_cdt_regalo') {
      return this.createBonusConfig(BonusType.CDT_REGALO, params);
    } else if (query_bono == 'bono_ruleta') {
      return this.createBonusConfig(BonusType.RULETA, params);
    } else if (query_bono == 'moneycon_2026') {
      return this.createBonusConfig(BonusType.MONEYCON_2026, params);
    }

    return null;
  }

  /**
   * Crea una configuración de bono basada en los parámetros
   */
  private createBonusConfig(bonusType: BonusType, params: any): BonusConfig {
    let amount = 0;

    if (params.amount) {
      amount = Number(params.amount);
    } else {
      amount = this.getAmountFromCookie();
    }
    const config: BonusConfig = {
      type: bonusType,
      amount: Number(amount),
      valorCdtRegalo: params.valor_cdt_regalo ? Number(params.valor_cdt_regalo) : undefined,
    };

    // Agregar minTermDays para CDT Regalo
    if (bonusType === BonusType.CDT_REGALO) {
      config.minTermDays = params.min_term_days ? Number(params.min_term_days) : 360;
    }

    return config;
  }

  /**
   * Obtiene la etiqueta apropiada para el tipo de bono
   */
  private getBonusTag(bonusType: BonusType): string {
    switch (bonusType) {
      case BonusType.BIENVENIDA:
        return 'bienvenida';
      case BonusType.ESCALONADO:
        return 'escalonado';
      case BonusType.PORCENTUAL:
        return 'porcentual';
      case BonusType.CDT_REGALO:
        return 'cdt-regalo';
      case BonusType.RULETA:
        return 'ruleta';
      case BonusType.YO_ME_LLAMO_CDT:
        return 'yo-me-llamo-cdt';
      case BonusType.DIAS_AZULES:
        return 'porcentual';
      case BonusType.IAF_2026:
        return 'porcentual';
      case BonusType.MONEYCON_2026:
        return 'porcentual';
      default:
        return '';
    }
  }

  /**
   * Formatea el monto para mostrar en la UI
   */
  private formatAmount(amount: number): string {
    return amount.toLocaleString('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  /**
   * Obtiene las restricciones de monto para un tipo de bono
   */
  getAmountConstraints(bonusType: BonusType, extraParams?: any): AmountConstraints | null {
    const strategy = this.strategies.get(bonusType);
    return strategy?.getAmountConstraints(extraParams) || null;
  }

  /**
   * Verifica si un tipo de bono es válido
   */
  isValidBonusType(bonusType: string): bonusType is BonusType {
    return Object.values(BonusType).includes(bonusType as BonusType);
  }

  /**
   * Obtiene los términos bloqueados para el bono activo
   * Retorna array de términos en días que están bloqueados
   */
  getBlockedTerms(): number[] {
    if (!this.activeBonusConfig) {
      return [];
    }

    const currentBonus = this.currentBonus$.value;
    return currentBonus?.minTermDays || [];
  }

  /**
   * Verifica si un término específico está bloqueado por el bono activo
   * @param termDays Término en días a verificar
   * @returns true si el término está bloqueado
   */
  isTermBlocked(termDays: number): boolean {
    const blockedTerms = this.getBlockedTerms();
    return blockedTerms.includes(termDays);
  }

  /**
   * Obtiene todas las estrategias disponibles (para debugging)
   */
  getAvailableStrategies(): BonusType[] {
    return Array.from(this.strategies.keys());
  }

  // ================== MÉTODOS PARA GESTIÓN DE TÉRMINOS ==================

  /**
   * Configura si los términos inválidos deben bloquearse (true) o permitir selección con desactivación del bono (false)
   * @param block true = bloquear términos, false = permitir selección pero desactivar bono
   */
  setBlockInvalidTermsMode(block: boolean): void {
    this.globalBlockInvalidTerms = block;
  }

  /**
   * Obtiene el modo actual de bloqueo de términos
   * @returns true si bloquea términos, false si permite selección con desactivación
   */
  getBlockInvalidTermsMode(): boolean {
    return this.globalBlockInvalidTerms;
  }

  /**
   * Verifica si un término específico está bloqueado por el bono activo
   * En modo advertencia (blockInvalidTerms = false), este método siempre retorna false para permitir selección
   * @param termDays Término en días a verificar
   * @returns true si el término debe estar bloqueado visualmente
   */
  isTermVisuallyBlocked(termDays: number): boolean {
    if (!this.globalBlockInvalidTerms) {
      return false; // En modo advertencia, no bloquear visualmente
    }

    const blockedTerms = this.getBlockedTerms();
    return blockedTerms.includes(termDays);
  }

  /**
   * Verifica si un término es incompatible con el bono actual (independiente del modo de bloqueo)
   * @param termDays Término en días a verificar
   * @returns true si el término es incompatible con el bono
   */
  isTermIncompatibleWithBonus(termDays: number): boolean {
    const blockedTerms = this.getBlockedTerms();
    return blockedTerms.includes(termDays);
  }

  /**
   * Desactiva el bono actual cuando se selecciona un término incompatible
   * @param termDays Término seleccionado que causó la desactivación
   */
  deactivateBonusForInvalidTerm(termDays: number): void {
    if (this.isTermIncompatibleWithBonus(termDays)) {
      // Guardar la configuración antes de limpiar
      this.storeBonusConfig(this.activeBonusConfig);

      this.clearBonus();
    }
  }

  /**
   * Reactiva el bono cuando se selecciona un término compatible
   * @param termDays Término seleccionado que podría reactivar el bono
   * @param currentAmount Monto actual de inversión para evaluar el bono
   */
  reactivateBonusForValidTerm(termDays: number, currentAmount?: number): void {
    // Si ya hay un bono activo, no hacer nada
    if (this.hasActiveBonus()) {
      return;
    }

    // Verificar si había una configuración de bono previamente guardada
    const storedBonusConfig = this.getStoredBonusConfig();
    if (!storedBonusConfig) {
      return;
    }

    // Verificar si el término actual es compatible con el bono almacenado
    if (this.isTermCompatibleWithStoredBonus(termDays, storedBonusConfig)) {
      // Restaurar la configuración del bono
      this.activeBonusConfig = storedBonusConfig;
      this.activeBonusConfig$.next(storedBonusConfig);

      // Evaluar el bono con el monto actual
      const finalAmount = currentAmount !== undefined ? currentAmount : this.getAmountFromCookie();
      this.evaluateBonusForAmount(finalAmount);
    }
  }

  /**
   * Verifica si un término es compatible con un bono almacenado
   * @param termDays Término en días
   * @param bonusConfig Configuración del bono almacenado
   * @returns true si es compatible
   */
  private isTermCompatibleWithStoredBonus(termDays: number, bonusConfig: BonusConfig): boolean {
    const strategy = this.strategies.get(bonusConfig.type);
    if (!strategy) {
      return false;
    }

    // Preparar extraParams según el tipo de bono
    const extraParams: any = {
      valorCdtRegalo: bonusConfig.valorCdtRegalo,
    };

    // Para bonos de ruleta, agregar el premio ganado
    if (bonusConfig.type === BonusType.RULETA && bonusConfig.roulettePrize) {
      extraParams.roulettePrize = bonusConfig.roulettePrize;
    }

    const testResult = strategy.calculateBonus(bonusConfig.amount, termDays, extraParams);
    if (testResult.minTermDays && testResult.minTermDays.includes(termDays)) {
      return false;
    }
    return true;
  }

  /**
   * Resuelve la configuración de bono final combinando query params y localStorage
   * Prioriza el localStorage cuando hay un contexto específico de campaña
   * @param queryConfig Configuración detectada desde query parameters
   * @param storedConfig Configuración almacenada en localStorage
   * @param queryParams Query parameters originales para contexto
   * @returns Configuración de bono final o null
   */
  private resolveHybridBonusConfig(
    queryConfig: BonusConfig | null,
    storedConfig: BonusConfig | null,
    queryParams: any,
  ): BonusConfig | null {
    // Si no hay query param 'bono', no activar nada
    if (!queryParams.bono) {
      return null;
    }

    // Si hay configuración almacenada con tipo específico (ej: YO_ME_LLAMO_CDT)
    // y query param genérico (ej: bono_escalonado), priorizar el almacenado
    if (storedConfig && queryConfig) {
      const storedIsSpecific = this.isCampaignSpecificBonus(storedConfig.type);
      const queryIsGeneric = this.isGenericBonus(queryConfig.type);

      if (storedIsSpecific && queryIsGeneric) {
        console.log(`Priorizando bono específico de localStorage: ${storedConfig.type} sobre ${queryConfig.type}`);
        // Mantener el tipo específico pero actualizar amount desde query/cookie
        return {
          ...storedConfig,
          amount: queryConfig.amount,
        };
      }
    }

    // Si solo hay query config, usarla directamente
    if (queryConfig && !storedConfig) {
      return queryConfig;
    }

    // Si solo hay stored config pero sin query 'bono', no activar
    if (!queryConfig && storedConfig) {
      return null;
    }

    // Fallback: usar query config
    return queryConfig;
  }

  /**
   * Verifica si un tipo de bono es específico de campaña
   * ESCALABLE: Consulta automáticamente la propiedad campaignSpecific de cada estrategia
   * @param bonusType Tipo de bono a verificar
   * @returns true si es específico de campaña
   */
  private isCampaignSpecificBonus(bonusType: BonusType): boolean {
    const strategy = this.strategies.get(bonusType);
    return strategy?.campaignSpecific ?? false;
  }

  /**
   * Verifica si un tipo de bono es genérico (puede aplicar a múltiples campañas)
   * ESCALABLE: Consulta automáticamente la propiedad campaignSpecific de cada estrategia
   * @param bonusType Tipo de bono a verificar
   * @returns true si es genérico
   */
  private isGenericBonus(bonusType: BonusType): boolean {
    const strategy = this.strategies.get(bonusType);
    return strategy?.campaignSpecific === false;
  }

  /**
   * Guarda la configuración del bono (tanto al activar como al desactivar)
   * @param config Configuración del bono a guardar
   */
  private storeBonusConfig(config: BonusConfig | null): void {
    if (config) {
      this.localStorage.save('mcdt_stored_bonus_config', JSON.stringify(config));
    }
  }

  /**
   * Obtiene la configuración del bono almacenada previamente
   * @returns Configuración del bono almacenado o null
   */
  public getStoredBonusConfig(): BonusConfig | null {
    try {
      const stored = this.localStorage.get('mcdt_stored_bonus_config');
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error al recuperar configuración de bono almacenada:', error);
      return null;
    }
  }

  /**
   * Limpia la configuración de bono almacenada
   */
  private clearStoredBonusConfig(): void {
    this.localStorage.delete('mcdt_stored_bonus_config');
  }

  /**
   * Obtiene el mensaje del tooltip para términos según el modo de bloqueo
   * @param termDays Término en días
   * @returns Mensaje apropiado según el modo (bloqueo vs advertencia)
   */
  getTermTooltipMessage(termDays: number): string {
    const termInMonths = Math.floor(termDays / 30);

    if (this.globalBlockInvalidTerms) {
      // Modo BLOQUEO: Término no disponible
      return `🎁 <b>Bono activo - Plazo de ${termInMonths} ${termInMonths === 1 ? 'mes' : 'meses'} no disponible</b><br>Este plazo no está disponible debido a las condiciones de tu bono activo. Solo puedes seleccionar plazos de 12 meses o más para aplicar al beneficio.`;
    } else {
      // Modo ADVERTENCIA: Término removerá el bono
      return `⚠️ <b>Seleccionar este plazo removerá tu bono</b><br>El plazo de ${termInMonths} ${termInMonths === 1 ? 'mes' : 'meses'} no es compatible con tu bono actual. Si lo seleccionas, tu bono se desactivará automáticamente.`;
    }
  }

  /**
   * Maneja automáticamente la activación/desactivación del bono según el término seleccionado
   * @param termDays Término seleccionado por el usuario
   * @param currentAmount Monto actual de inversión (opcional)
   */
  handleTermSelection(termDays: number, currentAmount?: number): void {
    // Si hay un bono activo y el término es incompatible, desactivar
    if (this.hasActiveBonus() && this.isTermIncompatibleWithBonus(termDays)) {
      this.deactivateBonusForInvalidTerm(termDays);
    }
    // Si no hay bono activo, intentar reactivar si es compatible
    else if (!this.hasActiveBonus()) {
      this.reactivateBonusForValidTerm(termDays, currentAmount);
    }
  }

  /**
   * Detecta un posible bono desde query params y retorna BonusResult completo
   * @param queryParams Objeto con los query parameters de la URL
   * @returns BonusResult completo con toda la información del bono o null si no hay bono
   */
  detectPossibleBonusResult(queryParams: any): BonusResult | null {
    const bonusConfig = this.detectBonusFromParams(queryParams);
    if (!bonusConfig) {
      return null;
    }

    const strategy = this.strategies.get(bonusConfig.type);
    if (!strategy) {
      return null;
    }

    // Calcular el bono completo usando la estrategia
    const extraParams = { valorCdtRegalo: bonusConfig.valorCdtRegalo };
    const termDays = bonusConfig.minTermDays || 360;
    const bonusResult = strategy.calculateBonus(bonusConfig.amount, termDays, extraParams);

    // Enriquecer con información adicional
    const enrichedResult: BonusResult = {
      ...bonusResult,
      bonusTag: this.getBonusTag(bonusConfig.type),
      displayAmount: bonusResult.displayAmount || this.formatAmount(bonusResult.bonusAmount),
      blockInvalidTerms: this.globalBlockInvalidTerms, // Incluir configuración de bloqueo
    };

    return enrichedResult;
  }

  // ================== MÉTODOS DE RULETA (DELEGADOS) ==================

  /**
   * Método de conveniencia para activar ruleta desde componentes
   * Delega al RouletteService para mantener compatibilidad
   * @param source Identificador del componente/campaña que solicita la ruleta
   * @param wheelOptions Opciones de la ruleta
   * @param userContext Contexto adicional del usuario (opcional)
   */
  async showWheelRoulette(source: string, wheelOptions: WheelOption[], userContext?: any): Promise<WheelOption | null> {
    return this.rouletteService.showWheelRoulette(source, wheelOptions, userContext);
  }

  /**
   * Procesa un premio de ruleta y lo convierte en bono cashback
   * TAMBIÉN activa la configuración de bono para que sea visible en otros componentes
   * @param roulettePrize Premio ganado en la ruleta
   * @param userAmount Monto de inversión del usuario
   * @param customTermsUrl URL personalizada de términos y condiciones (opcional)
   * @returns BonusResult con el cashback calculado
   */
  processRoulettePrize(roulettePrize: WheelOption, userAmount: number, customTermsUrl?: string): BonusResult {
    const ruletteStrategy = this.strategies.get(BonusType.RULETA);

    if (!ruletteStrategy) {
      console.error('Estrategia de ruleta no encontrada');
      return {
        isValid: false,
        bonusAmount: 0,
        message: 'Error procesando premio de ruleta',
        confettiType: '',
        bonusTag: 'ruleta-error',
      };
    }

    const rouletteBonusConfig: BonusConfig = {
      type: BonusType.RULETA,
      amount: userAmount,
      roulettePrize: roulettePrize,
    };

    // Activar la configuración de bono de ruleta
    this.activeBonusConfig = rouletteBonusConfig;
    this.activeBonusConfig$.next(rouletteBonusConfig);

    const extraParams = { roulettePrize, customTermsUrl };
    const termDays = 360;
    const bonusResult = ruletteStrategy.calculateBonus(userAmount, termDays, extraParams);

    // Enriquecer el resultado
    const enrichedResult: BonusResult = {
      ...bonusResult,
      displayAmount: bonusResult.displayAmount || this.formatAmount(bonusResult.bonusAmount),
      blockInvalidTerms: this.globalBlockInvalidTerms, // Incluir configuración de bloqueo
    };

    // Actualizar el estado del bono actual
    this.currentBonus$.next(enrichedResult);

    return enrichedResult;
  }

  /**
   * MÉTODOS DE DISPLAY - Centraliza la lógica de presentación de bonos
   */

  /**
   * Verifica si un bono es de tipo porcentual
   * @param bonusResult Resultado del bono a evaluar
   * @returns true si es porcentual (revisando bonusTag principal y roulettePrize.bonus_type/tag)
   */
  static isPercentageBonus(bonusResult: BonusResult | null): boolean {
    if (!bonusResult) {
      return false;
    }
    // Verificar bonusTag principal (ej: "porcentual", "ruleta-porcentual")
    const mainTagIsPercentage = bonusResult.bonusTag === 'porcentual';
    // Verificar bonusTag del premio de ruleta (ej: roulettePrize.tag === "porcentual")
    const rouletteTagIsPercentage = bonusResult.roulettePrize?.bonusTag === 'porcentual';
    return mainTagIsPercentage || rouletteTagIsPercentage;
  }

  /**
   * Obtiene el valor del bono formateado según el tipo
   * Para bonos porcentuales muestra el porcentaje, para otros muestra el monto
   * @param bonusResult Resultado del bono
   * @param currentBonusConfig Configuración actual del bono (opcional para cálculos porcentuales)
   * @returns string con el valor formateado
   */
  static getBonusDisplayValue(bonusResult: BonusResult | null, currentBonusConfig?: BonusConfig | null): string {
    if (!bonusResult) {
      return '';
    }

    // Para premio "La Mejor tasa del mercado", mostrar texto personalizado
    if (this.isBestRatePrize(bonusResult)) {
      return 'La mejor tasa del mercado';
    }

    // Si es un bono porcentual, obtener el valor/label del premio
    if (this.isPercentageBonus(bonusResult)) {
      // Para bonos de ruleta porcentuales, usar el valor del premio (ej: "+0.5%")
      if (bonusResult.roulettePrize?.value) {
        return bonusResult.roulettePrize.value; // "+0.5%"
      }

      // Para bonos de ruleta porcentuales, usar el label del premio (ej: "+0.5%")
      if (bonusResult.roulettePrize?.label) {
        return bonusResult.roulettePrize.label; // "+0.5%"
      }

      // Para bonos porcentuales básicos de estrategia, calcular desde el monto
      if (bonusResult.value) {
        return bonusResult.value; // 0.3
      }

      // Fallback: valor por defecto
      return '+0.25%';
    }

    // Para otros tipos de bono, mostrar el monto formateado sin símbolo
    return bonusResult.bonusAmount.toLocaleString('es-CO', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }

  /**
   * Obtiene el símbolo apropiado para el tipo de bono
   * @param bonusResult Resultado del bono
   * @returns string con el símbolo ($, vacío para porcentuales, etc.)
   */
  static getBonusSymbol(bonusResult: BonusResult | null): string {
    if (!bonusResult) {
      return '$';
    }

    // Para premio "La Mejor tasa del mercado", usar ícono de porcentaje
    if (this.isBestRatePrize(bonusResult)) {
      return '<i class="bi bi-percent text-success"></i>';
    }

    // Para bonos porcentuales no mostrar símbolo adicional (ya incluido en el valor)
    if (this.isPercentageBonus(bonusResult)) {
      return '';
    }

    // Para otros tipos de bono, mostrar el símbolo de peso
    return '$';
  }

  /**
   * Verifica si un bono es el premio especial "La Mejor tasa del mercado"
   * @param bonusResult Resultado del bono a evaluar
   * @returns true si es el premio especial de mejor tasa
   */
  static isBestRatePrize(bonusResult: BonusResult | null): boolean {
    if (!bonusResult || !bonusResult.roulettePrize) {
      return false;
    }

    const prize = bonusResult.roulettePrize;
    return (
      prize.bonus_type === 'MEJOR_TASA_MERCADO' ||
      prize.label?.toLowerCase().includes('mejor tasa') ||
      bonusResult.specialPrize === true
    );
  }

  /**
   * Obtiene la etiqueta apropiada para el tipo de bono
   * @param bonusResult Resultado del bono
   * @returns string con la etiqueta (Bono, Cashback)
   */
  static getBonusLabel(bonusResult: BonusResult | null): string {
    if (!bonusResult) {
      return 'Cashback';
    }

    // Para bonos porcentuales usar "Cashback"
    if (this.isPercentageBonus(bonusResult)) {
      return 'Cashback';
    }

    // Para otros tipos usar "Bono"
    return 'Bono';
  }

  /**
   * Método de conveniencia que retorna toda la información de display de una vez
   * @param bonusResult Resultado del bono
   * @param currentBonusConfig Configuración actual del bono (opcional)
   * @returns Objeto con toda la información de display
   */
  static getBonusDisplayInfo(
    bonusResult: BonusResult | null,
    currentBonusConfig?: BonusConfig | null,
  ): {
    value: string;
    symbol: string;
    label: string;
    isPercentage: boolean;
  } {
    return {
      value: this.getBonusDisplayValue(bonusResult, currentBonusConfig),
      symbol: this.getBonusSymbol(bonusResult),
      label: this.getBonusLabel(bonusResult),
      isPercentage: this.isPercentageBonus(bonusResult),
    };
  }
}
